import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import TenantSwitcher from './TenantSwitcher';
import LiveSyncStatusPill from './LiveSyncStatusPill';
import * as FiIcons from 'react-icons/fi';

const {
  FiGrid,
  FiFolder,
  FiLayers,
  FiDatabase,
  FiSettings,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiMoon,
  FiSun,
  FiPlus,
  FiUsers,
  FiMapPin,
  FiFileText,
  FiHelpCircle,
  FiLogOut
} = FiIcons;

/**
 * NavigationRail & AppLayout
 * Modern Linear/Vercel inspired navigation shell with collapsible rail & live status widgets
 */
export const AppLayout = ({
  children,
  onOpenCommandPalette,
  onOpenNewCertificate,
  pendingSyncCount = 0
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('saas_nav_collapsed') === 'true';
  });

  const { activeTenant } = useTenant();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('saas_nav_collapsed', String(next));
      return next;
    });
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: FiGrid },
    { name: 'Certificates', path: '/forms', icon: FiFolder },
    { name: 'Customers', path: '/customers', icon: FiUsers },
    { name: 'Properties', path: '/properties', icon: FiMapPin },
    { name: 'Templates', path: '/templates', icon: FiLayers },
    { name: 'Backup & Storage', path: '/backup', icon: FiDatabase },
    { name: 'Settings', path: '/settings', icon: FiSettings },
  ];

  const getBreadcrumbs = () => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return ['Dashboard'];
    if (path.startsWith('/customers')) return ['Workspace', 'CRM', 'Customers'];
    if (path.startsWith('/properties')) return ['Workspace', 'CRM', 'Properties'];
    if (path.startsWith('/forms')) return ['Workspace', 'Certificates'];
    if (path.startsWith('/templates')) return ['Workspace', 'Templates'];
    if (path.startsWith('/backup')) return ['System', 'Storage & Backups'];
    if (path.startsWith('/settings')) return ['Settings', 'Organization'];
    if (path.startsWith('/debug')) return ['Developer', 'Diagnostics'];
    return ['App'];
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50 dark:bg-[#0b0f19] text-slate-900 dark:text-slate-100 font-sans">
      {/* Left Navigation Rail */}
      <aside
        className={`flex flex-col bg-slate-900 text-slate-300 border-r border-slate-800 transition-all duration-300 ease-in-out relative z-30 select-none ${
          isCollapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Rail Top Branding */}
        <div className="flex items-center justify-between p-3 border-b border-slate-800/80 h-14">
          {!isCollapsed && (
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center font-black text-white shadow-md text-sm">
                CS
              </div>
              <div className="truncate">
                <span className="font-bold text-sm tracking-tight text-white block truncate">CertSaaS</span>
                <span className="text-[10px] text-slate-400 block -mt-1 font-mono">v2.0 OFFLINE</span>
              </div>
            </div>
          )}

          {isCollapsed && (
            <div className="w-full flex justify-center">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center font-black text-white shadow-md text-xs">
                CS
              </div>
            </div>
          )}
        </div>

        {/* Tenant Switcher Section */}
        <div className="p-2 border-b border-slate-800/60">
          <TenantSwitcher isCollapsed={isCollapsed} />
        </div>

        {/* Quick New Certificate Action */}
        <div className="p-2">
          {isCollapsed ? (
            <button
              onClick={onOpenNewCertificate || onOpenCommandPalette}
              className="w-full h-10 rounded-xl bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center shadow-lg transition-transform active:scale-95"
              title="New Certificate"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={onOpenNewCertificate || onOpenCommandPalette}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-md shadow-blue-900/30 transition-all active:scale-98"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>New Certificate</span>
            </button>
          )}
        </div>

        {/* Main Navigation Items */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                  isActive
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                } ${isCollapsed ? 'justify-center px-0' : ''}`
              }
              title={isCollapsed ? item.name : undefined}
            >
              <SafeIcon icon={item.icon} className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && <span className="truncate">{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse Toggle & Footer Controls */}
        <div className="p-2 border-t border-slate-800/80 space-y-1">
          {/* Quick Command Palette Button */}
          <button
            onClick={onOpenCommandPalette}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors ${
              isCollapsed ? 'justify-center px-0' : 'justify-between'
            }`}
            title="Search or Quick Jump (Cmd+K)"
          >
            <div className="flex items-center gap-2 truncate">
              <SafeIcon icon={FiSearch} className="w-4 h-4 text-slate-400" />
              {!isCollapsed && <span>Search</span>}
            </div>
            {!isCollapsed && <span className="kbd-shortcut bg-slate-800 text-slate-300 border-slate-700">⌘K</span>}
          </button>

          {/* Collapse Button */}
          <button
            onClick={toggleCollapse}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 transition-colors ${
              isCollapsed ? 'justify-center px-0' : ''
            }`}
            title={isCollapsed ? 'Expand Navigation (Cmd+B)' : 'Collapse Navigation (Cmd+B)'}
          >
            <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="w-4 h-4 text-slate-400" />
            {!isCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Workspace Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0b0f19]">
        {/* Top Header Bar */}
        <header className="h-14 border-b border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 flex items-center justify-between z-20 flex-shrink-0">
          {/* Left Breadcrumbs & Workspace tag */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium truncate">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && <span className="text-slate-300 dark:text-slate-600">/</span>}
                  <span className={idx === breadcrumbs.length - 1 ? 'text-slate-900 dark:text-slate-100 font-semibold' : ''}>
                    {crumb}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right Action Icons & Status Widgets */}
          <div className="flex items-center gap-3">
            {/* Command Palette Trigger */}
            <button
              onClick={onOpenCommandPalette}
              className="hidden sm:flex items-center gap-2 px-2.5 py-1 text-xs rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors"
            >
              <SafeIcon icon={FiSearch} className="w-3.5 h-3.5" />
              <span>Quick jump...</span>
              <span className="kbd-shortcut">⌘K</span>
            </button>

            {/* Real-time Sync Indicator */}
            <LiveSyncStatusPill pendingSyncCount={pendingSyncCount} />

            {/* Dark/Light Theme Quick Toggle */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} mode`}
            >
              <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="w-4 h-4" />
            </button>

            {/* Active User Avatar & Session Menu */}
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center text-[10px] font-black border-2 border-white dark:border-slate-700 shadow-sm hover:scale-105 transition-transform"
                title={user ? `${user.name || user.email} — click for session options` : 'Local Workspace — click for session options'}
              >
                {(user?.name || user?.email || 'ENG')
                  .split(' ')
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </button>

              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-10 z-40 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-2 space-y-1">
                    <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800 mb-1">
                      <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                        {user?.name || 'Local Workspace'}
                      </p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                        {user?.email || 'Offline session'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        navigate('/settings');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <SafeIcon icon={FiSettings} className="w-3.5 h-3.5" />
                      Organization Settings
                    </button>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        logout();
                        navigate('/login', { replace: true });
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                    >
                      <SafeIcon icon={FiLogOut} className="w-3.5 h-3.5" />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content Body Container */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
